import { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Area, AreaChart } from "recharts";
import { api, fmtMoney } from "@/lib/api";

const PERIODS = [
  { label: "1D", period: "1d", interval: "5m" },
  { label: "5D", period: "5d", interval: "15m" },
  { label: "1M", period: "1mo", interval: "1d" },
  { label: "3M", period: "3mo", interval: "1d" },
  { label: "6M", period: "6mo", interval: "1d" },
  { label: "1Y", period: "1y", interval: "1d" },
  { label: "5Y", period: "5y", interval: "1wk" },
];

export default function PriceChart({ symbol, height = 340 }) {
  const [bars, setBars] = useState([]);
  const [period, setPeriod] = useState(PERIODS[2]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!symbol) return;
    setLoading(true);
    api.get(`/market/history/${symbol}?period=${period.period}&interval=${period.interval}`)
      .then((r) => setBars(r.data.bars || []))
      .finally(() => setLoading(false));
  }, [symbol, period]);

  const first = bars[0]?.c;
  const last = bars[bars.length - 1]?.c;
  const up = last >= first;
  const color = up ? "#00C805" : "#FF3B30";

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
        <div>
          <div className="mono text-[10px] text-zinc-500 uppercase tracking-widest">Price History</div>
          <div className="text-sm text-zinc-300 mono mt-1">{symbol}</div>
        </div>
        <div className="flex gap-1">
          {PERIODS.map((p) => (
            <button
              key={p.label}
              onClick={() => setPeriod(p)}
              data-testid={`chart-period-${p.label}`}
              className={`px-2.5 py-1 text-[10px] mono uppercase tracking-widest rounded-sm border ${
                period.label === p.label
                  ? "border-white bg-white text-black"
                  : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex-1 p-2" style={{ minHeight: height }}>
        {loading || bars.length === 0 ? (
          <div className="h-full flex items-center justify-center text-zinc-600 mono text-xs">
            {loading ? "Loading…" : "No data"}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={height}>
            <AreaChart data={bars} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="fill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.25} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="t"
                tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717a" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                tickFormatter={(v) => {
                  const d = new Date(v);
                  return period.period === "1d" ? `${d.getHours()}:${String(d.getMinutes()).padStart(2, "0")}`
                    : `${d.getMonth() + 1}/${d.getDate()}`;
                }}
                minTickGap={40}
              />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fontFamily: "JetBrains Mono", fontSize: 10, fill: "#71717a" }}
                axisLine={{ stroke: "#27272a" }}
                tickLine={false}
                orientation="right"
                width={55}
                tickFormatter={(v) => `$${v.toFixed(2)}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#18181b",
                  border: "1px solid #27272a",
                  borderRadius: 2,
                  fontFamily: "JetBrains Mono",
                  fontSize: 11,
                }}
                labelFormatter={(v) => new Date(v).toLocaleString()}
                formatter={(v) => [fmtMoney(v), "Close"]}
              />
              <ReferenceLine y={first} stroke="#3f3f46" strokeDasharray="2 3" />
              <Area
                type="monotone"
                dataKey="c"
                stroke={color}
                strokeWidth={1.5}
                fill="url(#fill)"
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
